import { useState, useMemo, useCallback } from "react";
import Fuse from "fuse.js";
import api from "../../../utils/api";

/**
 * Normalize a transaction description for comparison
 * - Lowercase
 * - Trim whitespace
 * - Remove common suffixes like #123, *1234, store numbers
 * - Remove "Inc", "LLC", "Corp" suffixes
 */
function normalizeDescription(description) {
	if (!description) return "";

	let normalized = description
		.toLowerCase()
		.trim()
		// Remove store numbers like #1234, *1234, Store 123
		.replace(/[#*]\s*\d+/gi, "")
		.replace(/\bstore\s*\d+/gi, "")
		.replace(/\blocation\s*\d+/gi, "")
		// Remove transaction IDs (long numbers at end)
		.replace(/\s+\d{4,}$/g, "")
		// Remove common business suffixes
		.replace(/\s+(inc\.?|llc\.?|corp\.?|co\.?|ltd\.?)$/gi, "")
		// Remove extra whitespace
		.replace(/\s+/g, " ")
		.trim();

	return normalized;
}

/**
 * Create a display name from a group of descriptions
 * Uses the most common description or the shortest one
 */
function getGroupDisplayName(descriptions) {
	if (!descriptions || descriptions.length === 0) return "Unknown";

	// Count occurrences of each description
	const counts = {};
	descriptions.forEach((desc) => {
		counts[desc] = (counts[desc] || 0) + 1;
	});

	// Find the most common
	let maxCount = 0;
	let mostCommon = descriptions[0];
	for (const [desc, count] of Object.entries(counts)) {
		if (count > maxCount) {
			maxCount = count;
			mostCommon = desc;
		}
	}

	return mostCommon;
}

/**
 * Get the most common value from an array
 */
function getMostCommon(arr) {
	if (!arr || arr.length === 0) return null;
	const counts = {};
	arr.forEach((item) => {
		if (item) counts[item] = (counts[item] || 0) + 1;
	});

	let maxCount = 0;
	let mostCommon = arr[0];
	for (const [item, count] of Object.entries(counts)) {
		if (count > maxCount) {
			maxCount = count;
			mostCommon = item;
		}
	}
	return mostCommon;
}

/**
 * Hook to fetch and cluster recurring transactions
 * Data is only fetched/processed when loadData() is called
 */
export default function useRecurringGroups({ startDate, endDate, selectedAccounts = [] }) {
	const [lineItems, setLineItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [hasLoaded, setHasLoaded] = useState(false);

	// Store current filter values for processing
	const [appliedFilters, setAppliedFilters] = useState({
		startDate: null,
		endDate: null,
		selectedAccounts: [],
	});

	// Manual load function - call this when user clicks "Apply"
	const loadData = useCallback(async () => {
		setLoading(true);
		setError(null);
		setHasLoaded(true);

		// Capture current filter values
		setAppliedFilters({
			startDate,
			endDate,
			selectedAccounts,
		});

		try {
			const res = await api.get("/money/lineItems");
			setLineItems(res.data || []);
		} catch (err) {
			console.error("Failed to fetch line items:", err);
			setError("Failed to load transactions: " + err.message);
		} finally {
			setLoading(false);
		}
	}, [startDate, endDate, selectedAccounts]);

	// Filter and cluster transactions using applied filters (not live props)
	const groups = useMemo(() => {
		if (!lineItems || lineItems.length === 0) return [];

		const { startDate: filterStart, endDate: filterEnd, selectedAccounts: filterAccounts } = appliedFilters;

		// Filter by date range
		let filtered = lineItems.filter((item) => {
			const itemDate = new Date(item.date);
			const start = filterStart ? new Date(filterStart) : null;
			const end = filterEnd ? new Date(filterEnd) : null;

			if (start && itemDate < start) return false;
			if (end && itemDate > end) return false;
			return true;
		});

		// Filter by selected accounts (if any)
		if (filterAccounts && filterAccounts.length > 0) {
			filtered = filtered.filter((item) => filterAccounts.includes(item.account));
		}

		if (filtered.length === 0) return [];

		// Normalize descriptions and prepare for clustering
		const itemsWithNormalized = filtered.map((item) => ({
			...item,
			normalizedDesc: normalizeDescription(item.description),
		}));

		// Group items by exact normalized description first
		const exactGroups = {};
		itemsWithNormalized.forEach((item) => {
			const key = item.normalizedDesc || "unknown";
			if (!exactGroups[key]) {
				exactGroups[key] = [];
			}
			exactGroups[key].push(item);
		});

		// Get unique normalized descriptions for fuzzy matching
		const uniqueDescriptions = Object.keys(exactGroups).map((key) => ({
			key,
			description: key,
			count: exactGroups[key].length,
		}));

		// Use Fuse.js to find similar descriptions
		const fuse = new Fuse(uniqueDescriptions, {
			keys: ["description"],
			threshold: 0.4,
			includeScore: true,
		});

		// Build clusters of similar descriptions
		const visited = new Set();
		const clusters = [];

		uniqueDescriptions.forEach((item) => {
			if (visited.has(item.key)) return;

			// Find similar descriptions
			const matches = fuse.search(item.description);
			const cluster = [item.key];
			visited.add(item.key);

			matches.forEach((match) => {
				if (!visited.has(match.item.key) && match.score <= 0.4) {
					cluster.push(match.item.key);
					visited.add(match.item.key);
				}
			});

			clusters.push(cluster);
		});

		// Build final groups from clusters
		const finalGroups = clusters.map((cluster) => {
			// Collect all transactions in this cluster
			const transactions = cluster.flatMap((key) => exactGroups[key] || []);

			// Get all original descriptions
			const originalDescriptions = transactions.map((t) => t.description);

			// Calculate stats
			const amounts = transactions.map((t) => Math.abs(Number(t.transaction) || 0));
			const totalAmount = amounts.reduce((sum, val) => sum + val, 0);
			const avgAmount = transactions.length > 0 ? totalAmount / transactions.length : 0;

			// Get most common category
			const categories = transactions.map((t) => t.category);
			const mostCommonCategory = getMostCommon(categories);

			// Unique descriptions matched
			const uniqueMatched = [...new Set(originalDescriptions)];

			// Sort transactions by date descending
			const sortedTransactions = [...transactions].sort(
				(a, b) => new Date(b.date) - new Date(a.date)
			);

			return {
				id: cluster[0], // Use first normalized description as ID
				name: getGroupDisplayName(originalDescriptions),
				normalizedKey: cluster[0],
				matchedDescriptions: uniqueMatched,
				transactions: sortedTransactions,
				count: transactions.length,
				totalAmount,
				avgAmount,
				category: mostCommonCategory,
			};
		});

		// Sort groups by count within each category
		const sortedGroups = finalGroups.sort((a, b) => b.count - a.count);

		// Group by category
		const byCategory = {};
		sortedGroups.forEach((group) => {
			const cat = group.category || "Uncategorized";
			if (!byCategory[cat]) {
				byCategory[cat] = {
					category: cat,
					groups: [],
					totalAmount: 0,
					transactionCount: 0,
				};
			}
			byCategory[cat].groups.push(group);
			byCategory[cat].totalAmount += group.totalAmount;
			byCategory[cat].transactionCount += group.count;
		});

		// Convert to array and sort categories by total amount
		const categoryList = Object.values(byCategory).sort(
			(a, b) => b.totalAmount - a.totalAmount
		);

		return categoryList;
	}, [lineItems, appliedFilters]);

	// Get unique accounts from the actual data
	const availableAccounts = useMemo(() => {
		if (!lineItems || lineItems.length === 0) return [];
		return [...new Set(lineItems.map(item => item.account))].filter(Boolean).sort();
	}, [lineItems]);

	return {
		groups,
		loading,
		error,
		totalTransactions: lineItems.length,
		loadData,
		hasLoaded,
		availableAccounts,
	};
}
